import { useContext, useState } from "react";

import { GuestGoalOption } from "@/api/register.ts";
import ErrorPage from "@/components/ui/ErrorPage.tsx";
import Spinner from "@/components/ui/spinner";
import { MainContext } from "@/context/user-information/user-information-context";
import useRegistrationFormOptions from "@/features/register/hooks/useRegistrationFormOptions.ts";
import useUpdateGuestForm from "@/features/register/hooks/useUpdateGuestForm.ts";
import { useLoaderData, useNavigate } from "@tanstack/react-router";
import PriorityList from "../../components/_module/signup/priorityList";
import SignupHeader from "../../components/pages/signup/signupHeader";
import { Button } from "../../components/ui/button/button";
import { ArrowRightIcon } from "../../components/ui/icons";

const SignUpQuestionPage = () => {
	const { removeUserData } = useContext(MainContext);
	const navigate = useNavigate();
	const optionsQuery = useRegistrationFormOptions();
	const { name, location_id } = useLoaderData({ from: "__root__" });
	const guestForm = useUpdateGuestForm(location_id);
	const [selectedPriority, setSelectedPriority] = useState<
		GuestGoalOption | undefined
	>();

	function navigateToHome() {
		navigate({
			to: "/main/home",
		});
		removeUserData();
	}

	function handleSubmit() {
		if (selectedPriority) {
			guestForm.mutate(
				{ goal_option_id: selectedPriority.id },
				{
					onSuccess: () => {
						navigateToHome();
					},
					onError: () => {
						navigateToHome();
					},
				},
			);
		}
		// no need to handle the case where selectedPriority is undefined
		// as the button is disabled if selectedPriority is undefined
		// and the button is only enabled if selectedPriority is defined
	}

	if (optionsQuery.isError) {
		return (
			<ErrorPage
				message="Failed to load goal options"
				submitText="Skip and Go Home"
				onClose={() => navigateToHome()}
				onSubmit={() => navigateToHome()}
			/>
		);
	}

	return (
		<div className="flex flex-col items-center w-full max-w-[800px] mx-auto h-[100dvh] p-6 bg-white gap-6">
			<SignupHeader maxProgress={100} isBackButton={false} />
			<div className="mt-4 text-center">
				<h1 className="mb-2 text-2xl font-bold text-textDark">
					How can we help?
				</h1>
				<p className="text-sm font-medium text-center opacity-80 text-textLight">
					Select your main goal for using {name} today.
				</p>
			</div>
			<PriorityList
				priorityList={optionsQuery.data?.goal_options ?? []}
				selectedPriority={selectedPriority}
				setSelectedPriority={setSelectedPriority}
			/>
			<div className="flex justify-end w-full">
				<Button
					data-testid="question-continue-button"
					disabled={!selectedPriority || guestForm.status === "pending"}
					className="w-[58px] h-[58px] py-2 text-base text-white bg-primary-600 rounded-2xl"
					onClick={handleSubmit}
				>
					{guestForm.status === "pending" ? <Spinner /> : <ArrowRightIcon />}
				</Button>
			</div>
		</div>
	);
};

export default SignUpQuestionPage;
